import * as THREE from 'three'

/**
 * Pattern support notes
 * ---------------------
 * A "pattern" is a decorative image (e.g. a repeating artwork panel) printed
 * over a part's base color. The Product Profile viewer applies a section-wrap
 * shader to the exported GLB later, and that shader MULTIPLIES the base color
 * texture (`diffuseColor *= texture2D(map, vMapUv)`). A raw transparent PNG used
 * as the map would therefore turn transparent areas black and tint the artwork.
 *
 * To avoid interfering with that shader we FLATTEN the pattern over the base
 * color into a single opaque texture and set the material color to white. The
 * multiply then becomes `white * flattened = flattened`, and the section-wrap
 * overlay still composites cleanly on top. The flattened texture uses the same
 * geometry UVs the wrap shader reads, so the pattern wraps consistently.
 */

/** Load an image (data URL or object URL) into an HTMLImageElement. */
export function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Failed to load pattern image'))
    img.src = src
  })
}

const MAX_TEXTURE_SIZE = 2048
// Logo art is line work, so it gets more headroom than a pattern panel.
const MAX_LOGO_TEXTURE_SIZE = 4096
// three clamps this to whatever the GPU supports.
const MAX_ANISOTROPY = 16

/** Registration/tiling controls, all baked into the canvas so they survive export. */
export interface PatternAlignment {
  /** Horizontal tiling across the UV space (default 1 = wrap once). */
  repeatX?: number
  /** Vertical tiling across the UV space (default 1). */
  repeatY?: number
  /** Horizontal nudge as a fraction of circumference; wraps around the seam (default 0). */
  offsetX?: number
  /** Vertical nudge as a fraction of height; positive raises the artwork (default 0). */
  offsetY?: number
  /** Uniform zoom for fitting (default 1). */
  scale?: number
  /** Stretch to fill the whole part (may distort). False = preserve aspect ratio. */
  fill?: boolean
  /** The part's circumference:height ratio, used to keep the pattern undistorted when not filling. */
  objectAspect?: number
  /** Vertical placement of the unstretched band on the part. Default 'center'. */
  vAlign?: 'top' | 'center' | 'bottom' | 'object'
  /** Reference part's vertical position in this part's UV (0=top,1=bottom); used when vAlign='object'. */
  logoV?: number
}

/**
 * Composite a pattern image over a solid base color into an opaque canvas, baking
 * in tiling and registration offsets. Tiling wraps horizontally (the cylindrical
 * seam) so a horizontal offset simply rotates the artwork around the model. The
 * base color fills any transparent gaps in every tile.
 */
export function compositePatternCanvas(
  baseColorHex: string,
  image: HTMLImageElement,
  align: PatternAlignment = {}
): HTMLCanvasElement {
  const repeatX = align.repeatX && align.repeatX > 0 ? align.repeatX : 1
  const repeatY = align.repeatY && align.repeatY > 0 ? align.repeatY : 1
  const offsetX = align.offsetX ?? 0
  const offsetY = align.offsetY ?? 0
  const zoom = align.scale && align.scale > 0 ? align.scale : 1
  const fill = align.fill ?? false
  const imageAspect = image.width / Math.max(image.height, 1)

  // The canvas represents the UNROLLED surface (circumference : height). Sizing it to
  // the part's aspect means drawing the image at its own aspect ratio preserves the
  // artwork's proportions on the model. Without a known aspect, fall back to the image's.
  const targetAspect = align.objectAspect && align.objectAspect > 0 ? align.objectAspect : imageAspect
  let W: number
  let H: number
  if (targetAspect >= 1) {
    W = MAX_TEXTURE_SIZE
    H = Math.max(1, Math.round(MAX_TEXTURE_SIZE / targetAspect))
  } else {
    H = MAX_TEXTURE_SIZE
    W = Math.max(1, Math.round(MAX_TEXTURE_SIZE * targetAspect))
  }

  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')!
  // Opaque base color under the artwork (THREE.Color only understands #RRGGBB).
  ctx.fillStyle = baseColorHex.slice(0, 7)
  ctx.fillRect(0, 0, W, H)

  if (fill) {
    // Stretch one copy to cover the whole surface (may distort), with optional tiling.
    const pattern = ctx.createPattern(image, 'repeat')
    if (pattern) {
      const sx = (W / repeatX / image.width) * zoom
      const sy = (H / repeatY / image.height) * zoom
      pattern.setTransform(new DOMMatrix([sx, 0, 0, sy, offsetX * W, -offsetY * H]))
      ctx.fillStyle = pattern
      ctx.fillRect(0, 0, W, H)
    } else {
      ctx.drawImage(image, 0, 0, W, H)
    }
  } else {
    // ONE aspect-correct copy: it wraps horizontally (repeat-x) and sits as a single
    // band centred vertically — no vertical tiling. Base color fills above/below.
    const pattern = ctx.createPattern(image, 'repeat-x')
    if (pattern) {
      const tileW = (W / repeatX) * zoom
      const s = tileW / image.width // uniform scale → aspect preserved
      const bandH = image.height * s
      const tx = offsetX * W
      // Vertical placement of the band, then raised/lowered by offsetY.
      const vAlign = align.vAlign ?? 'center'
      let base: number
      if (vAlign === 'top') {
        base = 0
      } else if (vAlign === 'bottom') {
        base = H - bandH
      } else if (vAlign === 'object' && align.logoV !== undefined) {
        // Center the band on the reference part's height (clamped to stay on the part).
        base = Math.max(0, Math.min(H - bandH, align.logoV * H - bandH / 2))
      } else {
        base = (H - bandH) / 2
      }
      const ty = base - offsetY * H
      pattern.setTransform(new DOMMatrix([s, 0, 0, s, tx, ty]))
      ctx.fillStyle = pattern
      ctx.fillRect(0, 0, W, H)
    } else {
      const s = (W * zoom) / image.width
      ctx.drawImage(image, 0, (H - image.height * s) / 2, W * zoom, image.height * s)
    }
  }

  return canvas
}

/**
 * Texture for a logo PART (see utils/logoMesh.ts). Unlike a pattern, the artwork is
 * NOT flattened over a base color: the sheet is its own mesh, so the image's alpha is
 * kept and transparent areas are cut away by the material's alphaTest.
 *
 * Only the artwork's SHAPE is kept: every pixel is made white, alpha untouched. The
 * material multiplies its colour by this map, so with the artwork's own colours left in,
 * a black file stayed black and a white one white whatever the Base colour said — the
 * panel's colour could only darken the ink, never set it. As a white mask the logo is
 * exactly the Base colour, like every other part. Drawing it into
 * a canvas keeps it exportable (GLTFExporter serialises the canvas to a PNG with alpha)
 * and matches the flipY:false convention used everywhere else here.
 *
 * Logos are line art — thin strokes and small type — so this keeps more resolution than
 * a pattern does and asks for full anisotropic filtering; both keep the strokes from
 * dissolving into gray when the logo is small on screen.
 */
export function makeLogoTexture(image: HTMLImageElement, knockoutWhite = false): THREE.CanvasTexture {
  const scale = Math.min(1, MAX_LOGO_TEXTURE_SIZE / Math.max(image.width, image.height, 1))
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(image.width * scale))
  canvas.height = Math.max(1, Math.round(image.height * scale))
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height)
  if (knockoutWhite) removeWhiteBackground(ctx, canvas.width, canvas.height)
  whitenKeepingAlpha(ctx, canvas.width, canvas.height)

  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.wrapS = THREE.ClampToEdgeWrapping
  tex.wrapT = THREE.ClampToEdgeWrapping
  tex.flipY = false
  tex.anisotropy = MAX_ANISOTROPY
  tex.needsUpdate = true
  return tex
}

/** Make every pixel white, keeping its alpha — the artwork reduced to a coverage mask. */
function whitenKeepingAlpha(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  let data: ImageData
  try {
    data = ctx.getImageData(0, 0, width, height)
  } catch {
    return // a tainted canvas cannot be read; leave the artwork as it is
  }
  const px = data.data
  for (let i = 0; i < px.length; i += 4) {
    px[i] = 255
    px[i + 1] = 255
    px[i + 2] = 255
  }
  ctx.putImageData(data, 0, 0)
}

/**
 * Turn a white backdrop transparent. Plenty of logo files ship as opaque PNG/JPEG on
 * white; dropped onto a product as-is they read as a white card with the artwork on it
 * (invisible on a white part, and fighting the surface for depth). Pixels are graded
 * by their darkest channel so anti-aliased stroke edges keep partial coverage, and the
 * RGB is left alone so brand colors are untouched.
 */
function removeWhiteBackground(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const data = ctx.getImageData(0, 0, width, height)
  const px = data.data
  const CLEAR = 250 // at/above this the pixel is backdrop
  const KEEP = 235 // at/below this the pixel is artwork
  for (let i = 0; i < px.length; i += 4) {
    const darkest = Math.min(px[i], px[i + 1], px[i + 2])
    if (darkest >= CLEAR) px[i + 3] = 0
    else if (darkest > KEEP) px[i + 3] = Math.round((px[i + 3] * (CLEAR - darkest)) / (CLEAR - KEEP))
  }
  ctx.putImageData(data, 0, 0)
}

/**
 * True when the artwork has no transparency at all — the cue that it needs its white
 * backdrop knocked out to work as a decal.
 */
export function imageIsFullyOpaque(image: HTMLImageElement): boolean {
  const w = Math.min(image.width, 256)
  const h = Math.min(image.height, 256)
  if (!w || !h) return true
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) return true
  ctx.drawImage(image, 0, 0, w, h)
  try {
    const px = ctx.getImageData(0, 0, w, h).data
    for (let i = 3; i < px.length; i += 4) if (px[i] < 250) return false
    return true
  } catch {
    return true
  }
}

/** Artwork measured and cropped to what it actually draws. */
export interface PreparedArtwork {
  /** The artwork with empty margins removed. */
  image: string
  /** Aspect (width/height) of the visible mark, not of the file it arrived in. */
  aspect: number
  /** The source had no transparency at all, so a white backdrop should be cut out. */
  knockoutWhite: boolean
  /** True when margins were removed — the size fields then mean something different. */
  trimmed: boolean
  contentPx: { width: number; height: number }
  sourcePx: { width: number; height: number }
}

/** Largest analysis canvas; a bigger logo is measured from a downscaled copy. */
const MAX_ANALYSIS_DIM = 4096

/**
 * Measure a logo file and crop it to its visible artwork.
 *
 * Logo files routinely arrive as an artboard with the mark somewhere inside — an A4 page
 * at 96dpi, say, holding a badge a twentieth of its width. Sizing the sheet from the
 * file's own width and height then sizes the EMPTY SPACE: a mark filling a third of the
 * page comes out a third of the height asked for. Cropping to the ink first makes the
 * millimetre fields describe the thing the user actually measured.
 *
 * Only logos are treated this way. A pattern's margins are part of how it tiles, so
 * trimming one would change the artwork rather than just how it is measured.
 */
export async function prepareLogoArtwork(src: string): Promise<PreparedArtwork> {
  const image = await loadImageElement(src)
  const sourcePx = { width: image.width, height: image.height }
  const fallback: PreparedArtwork = {
    image: src,
    aspect: image.width / Math.max(image.height, 1),
    knockoutWhite: false,
    trimmed: false,
    contentPx: sourcePx,
    sourcePx,
  }
  if (!image.width || !image.height) return fallback

  // Measure from a downscaled copy when the file is very large; the crop itself is taken
  // at full resolution afterwards, so only the bounds lose a pixel of precision.
  const scale = Math.min(1, MAX_ANALYSIS_DIM / Math.max(image.width, image.height))
  const aw = Math.max(1, Math.round(image.width * scale))
  const ah = Math.max(1, Math.round(image.height * scale))

  const probe = document.createElement('canvas')
  probe.width = aw
  probe.height = ah
  const probeCtx = probe.getContext('2d', { willReadFrequently: true })
  if (!probeCtx) return fallback
  probeCtx.drawImage(image, 0, 0, aw, ah)

  let px: Uint8ClampedArray
  try {
    px = probeCtx.getImageData(0, 0, aw, ah).data
  } catch {
    return fallback // a tainted canvas — measure nothing rather than throw
  }

  // Artwork with no transparency anywhere is a mark on a white card, so whiteness marks
  // its background. Otherwise alpha does.
  let knockoutWhite = true
  for (let i = 3; i < px.length; i += 4) {
    if (px[i] < 250) {
      knockoutWhite = false
      break
    }
  }

  let minX = aw
  let minY = ah
  let maxX = -1
  let maxY = -1
  for (let y = 0; y < ah; y++) {
    for (let x = 0; x < aw; x++) {
      const i = (y * aw + x) * 4
      if (px[i + 3] <= 8) continue
      if (knockoutWhite && Math.min(px[i], px[i + 1], px[i + 2]) >= 250) continue
      if (x < minX) minX = x
      if (x > maxX) maxX = x
      if (y < minY) minY = y
      if (y > maxY) maxY = y
    }
  }
  if (maxX < 0) return { ...fallback, knockoutWhite } // nothing visible; leave it alone

  // Back to full-resolution pixels, rounded outwards so no ink is clipped.
  const inv = 1 / scale
  const left = Math.max(0, Math.floor(minX * inv))
  const top = Math.max(0, Math.floor(minY * inv))
  const right = Math.min(image.width, Math.ceil((maxX + 1) * inv))
  const bottom = Math.min(image.height, Math.ceil((maxY + 1) * inv))
  const cropW = Math.max(1, right - left)
  const cropH = Math.max(1, bottom - top)
  const contentPx = { width: cropW, height: cropH }

  if (cropW === image.width && cropH === image.height) {
    return { ...fallback, knockoutWhite, contentPx }
  }

  const cropped = document.createElement('canvas')
  cropped.width = cropW
  cropped.height = cropH
  const ctx = cropped.getContext('2d')
  if (!ctx) return { ...fallback, knockoutWhite, contentPx }
  ctx.drawImage(image, left, top, cropW, cropH, 0, 0, cropW, cropH)

  return {
    image: cropped.toDataURL('image/png'),
    aspect: cropW / cropH,
    knockoutWhite,
    trimmed: true,
    contentPx,
    sourcePx,
  }
}

/**
 * Build a THREE texture from a flattened pattern canvas. Tiling/offset are already
 * baked into the canvas, so the texture clamps and maps 1:1 to the UV space.
 * Defaults (flipY) are chosen so the viewer preview matches the exported GLB
 * round-trip through GLTFExporter/GLTFLoader.
 */
export function makePatternTexture(canvas: HTMLCanvasElement): THREE.CanvasTexture {
  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  // RepeatWrapping on U so the seam-split vertices (u slightly > 1) sample
  // continuously back into the artwork instead of clamping to a smeared edge.
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.ClampToEdgeWrapping
  // flipY:false matches the glTF texture convention (GLTFExporter/Loader) and the
  // baked pattern UVs (v = 1 - (y-minY)/height), so the preview, the exported GLB,
  // and the Product Profile viewer all show the artwork the same way up.
  tex.flipY = false
  tex.anisotropy = 8
  tex.needsUpdate = true
  return tex
}
